import { Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EndPoint } from '@constants/api.constant';
import { IResponse, Page, PageQuery } from '@models/common.model';
import { UserOderStatus, UserOrder } from '@models/orders.model';
import {
  GrossIncome,
  RegionUpdateRequest,
  RewardHistory,
  RewardHistoryPageQuery,
  SearchUserQuery,
  StatisticDetail,
  StatisticSummary,
  UserInfoBasic,
} from '@models/users.model';
import { Subject, map, switchMap, tap } from 'rxjs';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService {
  private loadStatistic$ = new Subject<string>();
  private _loadingStatistic = signal(false);
  private _statistic$ = this.loadStatistic$.pipe(
    tap(() => this._loadingStatistic.set(true)),
    switchMap(() => this.getUserStatisticSummary().pipe(map(x => x.data))),
    tap(() => this._loadingStatistic.set(false))
  );

  statistic = toSignal(this._statistic$);

  get statistic$() {
    return this._statistic$;
  }

  get loadingStatistic() {
    return this._loadingStatistic.asReadonly();
  }

  loadStatistic() {
    this.loadStatistic$.next('load');
  }

  getUserStatisticSummary() {
    return this.http.get<IResponse<StatisticSummary>>(
      EndPoint['userStatisticSummary'].path
    );
  }

  getUserStatisticDetail(params: PageQuery) {
    return this.http.get<IResponse<StatisticDetail>>(
      EndPoint['userStatisticDetail'].path,
      { params }
    );
  }

  getUserRewardHistories(params: RewardHistoryPageQuery) {
    return this.http.get<IResponse<Page<RewardHistory>>>(
      EndPoint['userRewardHistories'].path,
      { params }
    );
  }

  getUserGrossIncomes(params: PageQuery & { key?: string }) {
    return this.http.get<IResponse<Page<GrossIncome>>>(
      EndPoint['userGrossIncomes'].path,
      { params }
    );
  }

  getUserOrders(params: PageQuery & { status?: UserOderStatus }) {
    return this.http.get<IResponse<Page<UserOrder>>>(
      EndPoint['userOrders'].path,
      { params }
    );
  }

  cancelOrder(id: string) {
    return this.http.put<IResponse<boolean>>(
      `${EndPoint['userCancelOrder'].path}/${id}`,
      {}
    );
  }

  updateAvatar(id: number, request: { avatar: string }) {
    return this.http.put<IResponse<unknown>>(
      `${EndPoint['changeAvatar'].path}/${id}`,
      request
    );
  }

  updateUserProfile(request: Record<string, unknown>) {
    return this.http.put<IResponse<unknown>>(
      `${EndPoint['updateProfile'].path}`,
      request
    );
  }

  changePassword(id: number, request: Record<string, unknown>) {
    return this.http.post<IResponse<unknown>>(
      `${EndPoint['changePassword'].path}/${id}`,
      request
    );
  }

  getWallet(id = 0) {
    const postFix = id ? `/${id}` : '';
    return this.http.get<IResponse<number>>(
      `${EndPoint['userWallet'].path}${postFix}`
    );
  }

  searchUser(params: SearchUserQuery) {
    return this.http.get<IResponse<Page<UserInfoBasic>>>(
      EndPoint['searchUser'].path,
      { params }
    );
  }

  downloadQR(url: string, avatar: string, name: string) {
    return this.http.get<IResponse<string>>(EndPoint['downloadQR'].path, {
      params: { url, avatar, name },
    });
  }

  updateRegion(request: RegionUpdateRequest) {
    return this.http.post<IResponse<boolean>>(
      `${EndPoint['updateRegion'].path}`,
      request
    );
  }
}
